/**
 * What an alert is about and what its rule found (PRD-0075 R-B5): a watched entity, a phrase, a
 * new connection between two watched entities; a sample of the phrase, the keys that changed,
 * the nodes merged in, the watched entities a merge made one, the node a source came in under.
 * The inbox row composes both.
 */
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { formatVideoTime } from '@/utils/video-file'

const ALERT = 'cases.monitor.alert'

/** What an alert's rule found, as one line under the row. */
export type AlertFinding =
  | { kind: 'sample'; text: string }
  | { kind: 'changes'; keys: readonly string[] }
  | { kind: 'merged'; names: readonly string[] }
  /** Two or more watched entities that a merge made one (PRD-0075 SC-007). */
  | { kind: 'identity'; names: readonly string[] }
  /** An occurrence found under a node that a merge folded into the watched entity (R-B4). */
  | { kind: 'viaMerge'; name: string }

/** The parts of an inbox row these read. */
export interface AlertObjectRow {
  subjectLabel: string | null
  /** The phrase a phrase rule found. */
  phrase: string | null
  /** A new connection's other end, as the source named it. */
  otherLabel: string | null
  /** What a video event rule found in a recording: the event's kind, when it began, its verdict. */
  videoEvent?: { kind: string; tStart: number; verdict: string } | null
  finding: AlertFinding | null
}

/** The alert's object as text: the entity, the phrase in quotes, or the pair. */
export function alertObjectText(row: AlertObjectRow, t: TFunction): string {
  const unknown = t('cases.monitor.inbox.unknownSubject')
  if (row.videoEvent) {
    const events = 'documents.detail.videoEvents'
    return t(`${ALERT}.videoEvent`, {
      kind: t(`${events}.kinds.${row.videoEvent.kind}`, {
        defaultValue: t(`${events}.kinds.other`),
      }),
      at: formatVideoTime(row.videoEvent.tStart),
      verdict: t(`${events}.verdict.${row.videoEvent.verdict}`, {
        defaultValue: row.videoEvent.verdict,
      }),
    })
  }
  if (row.phrase !== null) return t(`${ALERT}.phrase`, { phrase: row.phrase })
  if (row.otherLabel !== null) {
    return t(`${ALERT}.connection`, {
      subject: row.subjectLabel ?? unknown,
      object: row.otherLabel,
    })
  }
  return row.subjectLabel ?? unknown
}

/** The alert's object: the entity, the phrase in quotes, or the pair. */
export function AlertObjectLabel({ row }: { row: AlertObjectRow }) {
  const { t } = useTranslation()
  return <>{alertObjectText(row, t)}</>
}

function findingText(finding: AlertFinding, t: TFunction): string {
  switch (finding.kind) {
    case 'sample':
      return finding.text
    case 'changes':
      return t(`${ALERT}.changedKeys`, { keys: finding.keys.join(', ') })
    case 'merged':
      return t(`${ALERT}.merged`, { names: finding.names.join(', ') })
    case 'identity':
      return t(`${ALERT}.identity`, { names: finding.names.join(', ') })
    case 'viaMerge':
      return t(`${ALERT}.viaMerge`, { name: finding.name })
  }
}

/** One line of what was found; nothing when the rule found no more than its object. */
export function AlertFindingLine({
  finding,
  full = false,
}: {
  finding: AlertFinding | null
  /** The whole text, as the detail shows it, in place of two lines. */
  full?: boolean
}) {
  const { t } = useTranslation()
  if (finding === null) return null
  const text = findingText(finding, t)
  return (
    <p
      className={cn('text-xs text-muted-foreground', full ? 'whitespace-pre-wrap' : 'line-clamp-2')}
      title={full ? undefined : text}
    >
      {text}
    </p>
  )
}
